/**
 * UIManager handles UI-related operations including mobile overlays,
 * responsive behavior, and device-specific interactions
 */
export class UIManager {
  constructor() {
    this.isMobile = this.detectMobile();
    this.overlays = new Map();
  }

  /**
   * Detect if device is mobile
   * @returns {boolean} True if mobile device
   */
  detectMobile() {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
           window.innerWidth <= 768;
  }





  /**
   * Initialize contact modal functionality
   */
  initContactModal() {
    const contactButtons = document.querySelectorAll('.contact-button');
    const modal = document.querySelector('.contact-modal');
    const closeButton = modal?.querySelector('.contact-modal-close');
    const overlay = modal?.querySelector('.contact-modal-overlay');

    if (!modal) {
      console.warn("Contact modal not found in DOM");
      return;
    }

    // Open modal
    contactButtons.forEach(button => {
      button.addEventListener('click', (e) => {
        e.preventDefault();
        this.showContactModal();
      });
    });

    // Close modal
    if (closeButton) {
      closeButton.addEventListener('click', () => {
        this.hideContactModal();
      });
    }

    if (overlay) {
      overlay.addEventListener('click', () => {
        this.hideContactModal();
      });
    }

    // Close on Escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && !modal.classList.contains('hidden')) {
        this.hideContactModal();
      }
    });
  }

  /**
   * Show contact modal
   */
  showContactModal() {
    const modal = document.querySelector('.contact-modal');
    if (modal) {
      modal.classList.remove('hidden');
      document.body.style.overflow = 'hidden';
    }
  }

  /**
   * Hide contact modal
   */
  hideContactModal() {
    const modal = document.querySelector('.contact-modal');
    if (modal) {
      modal.classList.add('hidden');
      document.body.style.overflow = '';
    }
  }

  /**
   * Initialize logo navigation
   * @param {Function} navigationCallback - Callback for navigation
   */
  initLogoNavigation(navigationCallback) {
    const logoLink = document.querySelector('.logo-link');
    if (logoLink) {
      logoLink.addEventListener("click", (e) => {
        e.preventDefault();
        navigationCallback(3);
      });
    }
  }

  /**
   * Setup keyboard navigation
   * @param {Function} nextStepCallback - Callback for next step
   * @param {Function} prevStepCallback - Callback for previous step
   */
  setupKeyboardNavigation(nextStepCallback, prevStepCallback) {
    document.addEventListener("keydown", (e) => {
      switch (e.key) {
        case "ArrowRight":
        case " ": // Spacebar
          e.preventDefault();
          nextStepCallback();
          break;
        case "ArrowLeft":
          e.preventDefault();
          prevStepCallback();
          break;
      }
    });
  }

  /**
   * Update UI for mobile/desktop differences
   * @param {HTMLElement} stepElement - Current step element
   */
  updateResponsiveUI(stepElement) {
    if (this.isMobile) {
      // Mobile-specific adjustments
      stepElement.classList.add('mobile-view');

      // Ensure mobile hotspots are visible
      const mobileHotspots = stepElement.querySelector('.mobile-hotspots');
      if (mobileHotspots) {
        mobileHotspots.style.display = 'flex';
      }

      // Hide desktop hotspots
      const desktopHotspots = stepElement.querySelectorAll('.hotspot');
      desktopHotspots.forEach(hotspot => {
        hotspot.style.display = 'none';
      });
    } else {
      // Desktop-specific adjustments
      stepElement.classList.remove('mobile-view');

      // Hide mobile hotspots
      const mobileHotspots = stepElement.querySelector('.mobile-hotspots');
      if (mobileHotspots) {
        mobileHotspots.style.display = 'none';
      }

      // Show desktop hotspots
      const desktopHotspots = stepElement.querySelectorAll('.hotspot');
      desktopHotspots.forEach(hotspot => {
        hotspot.style.display = 'flex';
      });
    }
  }

  /**
   * Create loading indicator
   * @param {string} message - Loading message
   * @returns {HTMLElement} Loading indicator element
   */
  createLoadingIndicator(message = "Loading...") {
    const indicator = document.createElement("div");
    indicator.className = "loading-indicator";
    indicator.style.cssText = `
      position: fixed;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      background: rgba(0, 0, 0, 0.8);
      color: white;
      padding: 20px;
      border-radius: 8px;
      z-index: 9999;
      text-align: center;
    `;
    indicator.textContent = message;
    return indicator;
  }

  /**
   * Show loading indicator
   * @param {string} message - Loading message
   */
  showLoading(message = "Loading...") {
    this.hideLoading(); // Remove any existing loading indicator

    const indicator = this.createLoadingIndicator(message);
    document.body.appendChild(indicator);
    this.overlays.set('loading', indicator);
  }

  /**
   * Hide loading indicator
   */
  hideLoading() {
    const indicator = this.overlays.get('loading');
    if (indicator && indicator.parentNode) {
      indicator.parentNode.removeChild(indicator);
      this.overlays.delete('loading');
    }
  }

  /**
   * Scroll to top of page (primarily for mobile step transitions)
   * @param {boolean} smooth - Whether to use smooth scrolling
   */
  scrollToTop(smooth = true) {
    console.log(`📱 ScrollToTop called - Mobile: ${this.isMobile}, Smooth: ${smooth}`);
    console.log(`📱 Window size: ${window.innerWidth}x${window.innerHeight}, UserAgent: ${navigator.userAgent.substring(0, 50)}...`);
    
    // Find the correct scrolling element
    let scrollElement = null;
    let currentScrollY = 0;
    
    // Always check stepper container first (mobile or desktop)
    const stepperContainer = document.querySelector('.stepper-container');
    if (stepperContainer) {
      console.log(`📱 Stepper container found, scrollTop: ${stepperContainer.scrollTop}px, scrollHeight: ${stepperContainer.scrollHeight}px, clientHeight: ${stepperContainer.clientHeight}px`);
      
      if (stepperContainer.scrollTop > 0 || stepperContainer.scrollHeight > stepperContainer.clientHeight) {
        scrollElement = stepperContainer;
        currentScrollY = stepperContainer.scrollTop;
        console.log(`📱 Using stepper container for scroll, scrollTop: ${currentScrollY}px`);
      }
    } else {
      console.log(`📱 No stepper container found`);
    }
    
    // Fallback to window/document scrolling
    if (!scrollElement) {
      scrollElement = window;
      currentScrollY = window.scrollY || document.documentElement.scrollTop || document.body.scrollTop;
      console.log(`📱 Using window scroll, scrollY: ${currentScrollY}px`);
    }
    
    if (currentScrollY === 0) {
      console.log(`📱 Already at top, no scroll needed`);
      return;
    }

    const scrollOptions = {
      top: 0,
      left: 0,
      behavior: smooth ? 'smooth' : 'instant'
    };

    try {
      if (scrollElement === window) {
        // Window scrolling
        if (window.scrollTo) {
          window.scrollTo(scrollOptions);
          console.log(`📱 Used window.scrollTo()`);
        } else {
          document.documentElement.scrollTop = 0;
          document.body.scrollTop = 0;
          console.log(`📱 Used fallback scrollTop`);
        }
      } else {
        // Container scrolling
        if (scrollElement.scrollTo) {
          scrollElement.scrollTo(scrollOptions);
          console.log(`📱 Used container.scrollTo()`);
        } else {
          scrollElement.scrollTop = 0;
          console.log(`📱 Used container.scrollTop`);
        }
      }

      // Verification after scroll attempt
      setTimeout(() => {
        let newScrollY = 0;
        if (scrollElement === window) {
          newScrollY = window.scrollY || document.documentElement.scrollTop || document.body.scrollTop;
        } else {
          newScrollY = scrollElement.scrollTop;
        }
        
        if (newScrollY > 0) {
          console.log(`📱 First attempt failed, trying force scroll. Current: ${newScrollY}px`);
          // Force scroll with all methods
          if (scrollElement === window) {
            document.documentElement.scrollTop = 0;
            document.body.scrollTop = 0;
            window.scrollTo(0, 0);
          } else {
            scrollElement.scrollTop = 0;
          }
        } else {
          console.log(`📱 Scroll successful! Final position: ${newScrollY}px`);
        }
      }, smooth ? 100 : 0);

    } catch (error) {
      console.error(`📱 ScrollToTop error:`, error);
    }
  }

  /**
   * Get device and UI statistics
   * @returns {Object} UI statistics
   */
  getUIStats() {
    return {
      isMobile: this.isMobile,
      screenWidth: window.innerWidth,
      screenHeight: window.innerHeight,
      userAgent: navigator.userAgent,
      activeOverlays: Array.from(this.overlays.keys())
    };
  }

  /**
   * Reset UI manager state
   */
  reset() {
    // Remove all overlays
    for (const [key, overlay] of this.overlays.entries()) {
      if (overlay.parentNode) {
        overlay.parentNode.removeChild(overlay);
      }
    }
    this.overlays.clear();
  }
}

// Export singleton instance
export const uiManager = new UIManager();
